import {
  CampaignCreationCapabilitiesSchema,
  type CampaignCreationRequestStatus,
  CampaignCreationRequestStatusSchema,
  type CampaignCreationSubmit,
  type SponsoredBrand,
  type SponsoredBrandAsset,
  SponsoredBrandAssetSchema,
  SponsoredBrandProductEligibilitySchema,
  type SponsoredBrandRecommendation,
  SponsoredBrandRecommendationSchema,
  SponsoredBrandSchema,
  type SponsoredBrandStore,
  SponsoredBrandStoreSchema,
  type SponsoredBrandsCreationSubmit,
  type SponsoredDisplayAudienceTargetSchema,
  SponsoredDisplayCapabilitiesSchema,
  type SponsoredDisplayContextualTargetSchema,
  type SponsoredDisplayCreationSubmit,
  type SponsoredDisplayOptimizationGoalSchema,
} from "@adpilot/shared";
import { z } from "zod";
import { api } from "@/lib/api";

const dataEnvelope = <T extends z.ZodTypeAny>(schema: T) =>
  z.object({ success: z.literal(true), data: schema });

function shopHeaders(shopId: string) {
  return { "X-Shop-Id": shopId };
}

const SpTargetCategoryCandidateSchema = z.object({
  id: z.string().regex(/^\d+$/),
  name: z.string().min(1),
  path: z.string().min(1),
});
const SpCategoryBrandCandidateSchema = z.object({
  id: z.string().regex(/^\d+$/),
  name: z.string().min(1),
});
const SpTargetCategorySearchResponseSchema = z.object({
  success: z.literal(true),
  data: z.array(SpTargetCategoryCandidateSchema),
  pagination: z.object({
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1).max(50),
    total: z.number().int().nonnegative(),
    hasMore: z.boolean(),
  }),
});

export type SpTargetCategoryCandidate = z.infer<typeof SpTargetCategoryCandidateSchema>;
export type SpCategoryBrandCandidate = z.infer<typeof SpCategoryBrandCandidateSchema>;

export async function searchSpTargetCategories(
  shopId: string,
  rawQuery: string,
  page = 1,
  pageSize = 20,
) {
  const q = z.string().trim().min(2).max(100).parse(rawQuery);
  const response = await api
    .get("campaign-creation/sponsored-products/target-categories", {
      headers: shopHeaders(shopId),
      searchParams: { q, page, pageSize },
      retry: 0,
    })
    .json<unknown>();
  return SpTargetCategorySearchResponseSchema.parse(response);
}

export async function fetchSpCategoryBrands(
  shopId: string,
  rawCategoryId: string,
): Promise<SpCategoryBrandCandidate[]> {
  const categoryId = z.string().regex(/^\d+$/).parse(rawCategoryId);
  const response = await api
    .get(`campaign-creation/sponsored-products/target-categories/${categoryId}/brands`, {
      headers: shopHeaders(shopId),
      retry: 0,
    })
    .json<unknown>();
  return dataEnvelope(z.array(SpCategoryBrandCandidateSchema)).parse(response).data;
}

export async function fetchCampaignCreationCapabilities(shopId: string) {
  const response = await api
    .get("campaign-creation/capabilities", { headers: shopHeaders(shopId) })
    .json<unknown>();
  return dataEnvelope(CampaignCreationCapabilitiesSchema).parse(response).data;
}

export async function fetchSponsoredBrandsEligibility(shopId: string): Promise<boolean> {
  const response = await api
    .get("campaign-creation/sponsored-brands/eligibility", {
      headers: shopHeaders(shopId),
      retry: 0,
    })
    .json<unknown>();
  return dataEnvelope(z.object({ eligible: z.literal(true) })).parse(response).data.eligible;
}

export async function fetchSponsoredDisplayCapabilities(shopId: string) {
  const response = await api
    .get("campaign-creation/sponsored-display/capabilities", {
      headers: shopHeaders(shopId),
      retry: 0,
    })
    .json<unknown>();
  return dataEnvelope(SponsoredDisplayCapabilitiesSchema).parse(response).data;
}

export async function submitCampaignCreation(
  payload: CampaignCreationSubmit | SponsoredBrandsCreationSubmit | SponsoredDisplayCreationSubmit,
  shopId: string,
): Promise<CampaignCreationRequestStatus> {
  const response = await api
    .post("campaign-creation/submit", {
      json: payload,
      retry: 0,
      headers: shopHeaders(shopId),
    })
    .json<unknown>();
  return dataEnvelope(CampaignCreationRequestStatusSchema).parse(response).data;
}

const SponsoredDisplayFeatureCapabilitySchema =
  SponsoredDisplayCapabilitiesSchema.shape.amazonAccess;

const SponsoredDisplayProductEligibilityResultSchema = z.object({
  capability: SponsoredDisplayFeatureCapabilitySchema,
  products: z.array(
    z.object({
      asin: z.string().regex(/^[A-Z0-9]{10}$/),
      sku: z.string().nullable(),
      status: z.enum(["ELIGIBLE", "ELIGIBLE_WITH_WARNING", "INELIGIBLE"]),
      messages: z.array(z.string()),
    }),
  ),
});

const SponsoredDisplayContextualRecommendationsSchema = z.object({
  capability: SponsoredDisplayFeatureCapabilitySchema,
  products: z.object({
    items: z.array(z.object({ asin: z.string().regex(/^[A-Z0-9]{10}$/), rank: z.number() })),
    truncated: z.boolean(),
  }),
  categories: z.object({
    items: z.array(
      z.object({
        categoryId: z.string(),
        name: z.string(),
        path: z.array(z.string()),
        targetableAsinCountRange: z
          .object({ min: z.number().nullable(), max: z.number().nullable() })
          .nullable(),
        rank: z.number(),
      }),
    ),
    truncated: z.boolean(),
  }),
});

const SponsoredDisplayAudienceRecommendationsSchema = z.object({
  capability: SponsoredDisplayFeatureCapabilitySchema,
  categories: z.array(
    z.object({
      category: z.enum(["in_market", "lifestyle", "interest", "life_event"]),
      items: z.array(z.object({ audienceId: z.string(), name: z.string(), rank: z.number() })),
      truncated: z.boolean(),
    }),
  ),
});

const SponsoredDisplayBidRecommendationsSchema = z.object({
  capability: SponsoredDisplayFeatureCapabilitySchema,
  currencyCode: z.string().nullable(),
  costType: z.enum(["cpc", "vcpm"]).nullable(),
  recommendations: z.array(
    z.union([
      z.object({
        index: z.number().int().nonnegative(),
        state: z.literal("available"),
        source: z.literal("amazon_ads_api"),
        recommended: z.number().positive(),
        range: z.object({ min: z.number().positive(), max: z.number().positive() }),
      }),
      z.object({
        index: z.number().int().nonnegative(),
        state: z.literal("unavailable"),
        reason: z.string(),
      }),
    ]),
  ),
});

export type SponsoredDisplayProductEligibilityResult = z.infer<
  typeof SponsoredDisplayProductEligibilityResultSchema
>;
export type SponsoredDisplayContextualRecommendations = z.infer<
  typeof SponsoredDisplayContextualRecommendationsSchema
>;
export type SponsoredDisplayAudienceRecommendations = z.infer<
  typeof SponsoredDisplayAudienceRecommendationsSchema
>;
export type SponsoredDisplayBidRecommendations = z.infer<
  typeof SponsoredDisplayBidRecommendationsSchema
>;

export async function fetchSponsoredDisplayAssets(shopId: string): Promise<SponsoredBrandAsset[]> {
  const response = await api
    .get("campaign-creation/sponsored-display/assets", {
      headers: shopHeaders(shopId),
      retry: 0,
    })
    .json<unknown>();
  return dataEnvelope(z.array(SponsoredBrandAssetSchema)).parse(response).data;
}

export async function fetchSponsoredDisplayProductEligibility(
  shopId: string,
  products: Array<{ asin: string; sku?: string | null }>,
): Promise<SponsoredDisplayProductEligibilityResult> {
  const response = await api
    .post("campaign-creation/sponsored-display/product-eligibility", {
      headers: shopHeaders(shopId),
      json: { products },
      retry: 0,
    })
    .json<unknown>();
  return dataEnvelope(SponsoredDisplayProductEligibilityResultSchema).parse(response).data;
}

export async function fetchSponsoredDisplayContextualRecommendations(
  shopId: string,
  advertisedAsins: string[],
): Promise<SponsoredDisplayContextualRecommendations> {
  const response = await api
    .post("campaign-creation/sponsored-display/contextual-recommendations", {
      headers: shopHeaders(shopId),
      json: { advertisedAsins },
      retry: 0,
    })
    .json<unknown>();
  return dataEnvelope(SponsoredDisplayContextualRecommendationsSchema).parse(response).data;
}

export async function fetchSponsoredDisplayAudienceRecommendations(
  shopId: string,
): Promise<SponsoredDisplayAudienceRecommendations> {
  const response = await api
    .post("campaign-creation/sponsored-display/audience-recommendations", {
      headers: shopHeaders(shopId),
      json: {},
      retry: 0,
    })
    .json<unknown>();
  return dataEnvelope(SponsoredDisplayAudienceRecommendationsSchema).parse(response).data;
}

export async function fetchSponsoredDisplayBidRecommendations(
  shopId: string,
  payload: {
    advertisedAsins: string[];
    optimizationGoal: z.infer<typeof SponsoredDisplayOptimizationGoalSchema>;
    creativeType: "IMAGE" | "VIDEO" | null;
    targets: Array<z.infer<typeof SponsoredDisplayContextualTargetSchema>>;
  },
): Promise<SponsoredDisplayBidRecommendations> {
  const response = await api
    .post("campaign-creation/sponsored-display/bid-recommendations", {
      headers: shopHeaders(shopId),
      json: payload,
      retry: 0,
    })
    .json<unknown>();
  return dataEnvelope(SponsoredDisplayBidRecommendationsSchema).parse(response).data;
}

export async function fetchSponsoredDisplayAudienceBidRecommendations(
  shopId: string,
  payload: {
    advertisedAsins: string[];
    optimizationGoal: z.infer<typeof SponsoredDisplayOptimizationGoalSchema>;
    creativeType: "IMAGE" | "VIDEO" | null;
    targets: Array<z.infer<typeof SponsoredDisplayAudienceTargetSchema>>;
  },
): Promise<SponsoredDisplayBidRecommendations> {
  const response = await api
    .post("campaign-creation/sponsored-display/audience-bid-recommendations", {
      headers: shopHeaders(shopId),
      json: payload,
      retry: 0,
    })
    .json<unknown>();
  return dataEnvelope(SponsoredDisplayBidRecommendationsSchema).parse(response).data;
}

export interface SponsoredBrandsDiscovery {
  brands: SponsoredBrand[];
  stores: SponsoredBrandStore[];
  assets: SponsoredBrandAsset[];
}

const SponsoredBrandsDiscoverySchema = z.object({
  brands: z.array(SponsoredBrandSchema),
  stores: z.array(SponsoredBrandStoreSchema),
  assets: z.array(SponsoredBrandAssetSchema),
});

export async function fetchSponsoredBrandsDiscovery(
  shopId: string,
): Promise<SponsoredBrandsDiscovery> {
  const response = await api
    .get("campaign-creation/sponsored-brands/discovery", {
      headers: shopHeaders(shopId),
      retry: 0,
    })
    .json<unknown>();
  return dataEnvelope(SponsoredBrandsDiscoverySchema).parse(response).data;
}

export async function fetchSponsoredBrandProductEligibility(
  shopId: string,
  products: { asin: string; sku?: string | null }[],
) {
  const response = await api
    .post("campaign-creation/sponsored-brands/product-eligibility", {
      headers: shopHeaders(shopId),
      json: { products },
      retry: 0,
    })
    .json<unknown>();
  return dataEnvelope(z.array(SponsoredBrandProductEligibilitySchema)).parse(response).data;
}

export async function fetchSponsoredBrandRecommendations(
  shopId: string,
  type: "keyword" | "product" | "category",
  asins: string[],
  adFormat: "productCollection" | "storeSpotlight" | "video",
): Promise<SponsoredBrandRecommendation[]> {
  const response = await api
    .post("campaign-creation/sponsored-brands/recommendations", {
      headers: shopHeaders(shopId),
      json: { type, asins, adFormat },
      retry: 0,
    })
    .json<unknown>();
  return dataEnvelope(z.array(SponsoredBrandRecommendationSchema)).parse(response)
    .data as SponsoredBrandRecommendation[];
}

export async function fetchCampaignCreationStatus(
  requestId: string,
  shopId: string,
): Promise<CampaignCreationRequestStatus> {
  const response = await api
    .get(`campaign-creation/requests/${encodeURIComponent(requestId)}`, {
      retry: 0,
      headers: shopHeaders(shopId),
    })
    .json<unknown>();
  return dataEnvelope(CampaignCreationRequestStatusSchema).parse(response).data;
}

export async function resumeCampaignCreation(
  requestId: string,
  shopId: string,
): Promise<CampaignCreationRequestStatus> {
  const response = await api
    .post(`campaign-creation/requests/${encodeURIComponent(requestId)}/resume`, {
      retry: 0,
      headers: shopHeaders(shopId),
    })
    .json<unknown>();
  return dataEnvelope(CampaignCreationRequestStatusSchema).parse(response).data;
}
